import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';

interface NavigationItem {
  label: string;
  path: string;
}

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const navigationItems: NavigationItem[] = [
    { label: t('header.home'), path: '/hero-section' },
    { label: t('header.about'), path: '/about-section' },
    { label: t('header.portfolio'), path: '/portfolio-showcase' },
    { label: t('header.testimonials'), path: '/testimonials-section' },
    { label: t('header.contact'), path: '/contact-section' },
  ];

  const toggleLanguage = () => {
    const nextLang = i18n.language?.startsWith('ar') ? 'en' : 'ar';
    i18n.changeLanguage(nextLang);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const isActivePath = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'glass-navbar-scrolled' : 'glass-navbar'
        } ${className}`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 md:px-6 lg:px-8">
          <Link
            to="/hero-section"
            className="flex items-center gap-3 group"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div className="relative">
              <div className="w-14 h-14 flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                <img src='/logo.png' alt='logo' className="w-full h-full object-contain p-1 drop-shadow-[0_0_15px_rgba(52,183,209,0.5)] group-hover:drop-shadow-[0_0_25px_rgba(52,183,209,0.8)] transition-all duration-300" />
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 [text-shadow:0_1px_3px_rgba(0,0,0,0.6)] ${isActivePath(item.path)
                  ? 'text-accent [text-shadow:0_0_15px_rgba(52,183,209,0.8)]'
                  : 'text-white/80 hover:text-white hover:bg-white/8'
                  }`}
              >
                {isActivePath(item.path) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-accent" />
                )}
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white/80 hover:text-white border border-white/10 hover:border-accent/40 hover:bg-white/8 transition-all duration-300"
            >
              <Icon name="Languages" size={16} color="currentColor" />
              {i18n.language?.startsWith('ar') ? 'English' : 'العربية'}
            </button>
            <button
              onClick={() => window.open('https://github.com/BMTmohammedtaha', '_blank')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white/80 hover:text-white border border-white/10 hover:border-accent/40 hover:bg-white/8 transition-all duration-300"
            >
              <Icon name="Github" size={16} color="currentColor" />
              {t('header.github')}
            </button>
            <button
              onClick={() => (window.location.href = '/contact-section')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white gradient-cta hover:scale-105 hover:shadow-brand-lg transition-all duration-300"
            >
              <Icon name="Mail" size={16} color="white" />
              {t('header.getInTouch')}
            </button>
          </div>

          <button
            className="lg:hidden p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors border border-white/10"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'X' : 'Menu'}
              size={22}
              color="currentColor"
            />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 glass-navbar-scrolled z-40 border-t border-white/10 ">
          <nav className="flex flex-col p-6 gap-2 glass-navbar-scrolled bg-black/50 ">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${isActivePath(item.path)
                  ? 'text-accent bg-accent/10 border border-accent/20'
                  : 'text-white/80 hover:text-white hover:bg-white/8 border border-transparent'
                  }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="flex flex-col gap-3 mt-6 pt-6 border-t border-white/10">
              <button
                onClick={() => { toggleLanguage(); setIsMobileMenuOpen(false); }}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-medium text-white/80 border border-white/15 hover:border-accent/40 hover:bg-white/8 transition-all duration-300"
              >
                <Icon name="Languages" size={18} color="currentColor" />
                {i18n.language?.startsWith('ar') ? 'English' : 'العربية'}
              </button>
              <button
                onClick={() => { window.open('https://github.com/BMTmohammedtaha', '_blank'); setIsMobileMenuOpen(false); }}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-medium text-white/80 border border-white/15 hover:border-accent/40 hover:bg-white/8 transition-all duration-300"
              >
                <Icon name="Github" size={18} color="currentColor" />
                {t('header.github')}
              </button>
              <button
                onClick={() => { window.location.href = '/contact-section'; setIsMobileMenuOpen(false); }}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-semibold text-white gradient-cta hover:shadow-brand-lg transition-all duration-300"
              >
                <Icon name="Mail" size={18} color="white" />
                {t('header.getInTouch')}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;